/**
 * launchLiveStreaming — permission-gated launch helper for the community live
 * session, called by BOTH entry points: the broadcaster "GO LIVE" flow (Task 15)
 * and the audience "Join Live" tile (Task 16).
 *
 * Web port of Flutter `_joinLiveStream` (`community_screen.dart:834-865`): the
 * shared join path requests `[Permission.camera, Permission.microphone]` and, if
 * any status is restricted / denied / permanently-denied, shows a "grant
 * permission" toast and does NOT navigate (`:851-855`); otherwise it navigates
 * (`:856-863`).
 *
 * Web deviation (deliberate + documented): only the BROADCASTER probes camera +
 * mic. Flutter's shared `_joinLiveStream` requests permission for both roles, but
 * the live audience publishes nothing (`useLiveStreamingAgora` acquires no local
 * tracks for `setClientRole('audience')`), so prompting a spectator for a camera
 * is a worse web UX and unnecessary. The audience therefore SKIPS the
 * `getUserMedia` probe and navigates directly.
 *
 * On the web the broadcaster permission request is the browser prompt fired by
 * `navigator.mediaDevices.getUserMedia({ video: true, audio: true })`. A thrown
 * error (`NotAllowedError` for a denied prompt, `NotFoundError` for a missing
 * device, etc.) is the web analog of Flutter's denied / restricted statuses — we
 * toast and bail without navigating. On grant we immediately stop the probe
 * tracks so the camera/mic indicator does not linger before the page mounts; the
 * `LiveStreamingPage` re-acquires the devices via the Agora hooks
 * (`useLiveStreamingAgora`).
 *
 * This is a plain presentation utility — it does NOT call React hooks. The caller
 * (the community page/sheet, which already has `useLocale()`) passes the resolved
 * `l10n` and the full live context, exactly as Flutter threads it through
 * `NavigationService.navigateToLiveStreamingScreen`.
 */
import toast from 'react-hot-toast';

import { appLogger } from '@/domain/services/analytics/appLogger';
import { Navigator } from '@/presentation/routing/Navigator';
import type { l10n } from '@/presentation/locale/l10n';
import type { LiveStreamingRouteArgs } from '@/presentation/feature/liveStreaming/args/liveStreamingRouteArgs';

/**
 * Args mirror the Flutter `_joinLiveStream` / `navigateToLiveStreamingScreen`
 * data payload plus the two web-only fields:
 * - `asBroadcaster` selects the permission-probe branch (broadcaster) vs. the
 *   view-only direct-navigate branch (audience);
 * - `l10n` supplies the toast copy (no hooks in this plain helper).
 */
export interface LaunchLiveStreamingArgs extends LiveStreamingRouteArgs {
  /** True for the "GO LIVE" broadcaster path; false for the "Join Live" audience path. */
  asBroadcaster: boolean;
  /** Resolved localizations from the caller (no hooks in this plain helper). */
  l10n: l10n;
}

/**
 * Probe camera + mic permission (broadcaster only), then navigate to the live
 * session screen.
 *
 * Parity with Flutter `_joinLiveStream` (`community_screen.dart:834-865`):
 * - broadcaster grant / audience → `Navigator.navigateToLiveStreaming(...)`
 *   (web analog of `NavigationService.navigateToLiveStreamingScreen`, `:856-863`).
 * - broadcaster denial → toast `permissionDeniedToast` and return WITHOUT
 *   navigating (`:851-855`).
 */
export async function launchLiveStreaming(
  args: LaunchLiveStreamingArgs,
): Promise<void> {
  const {
    asBroadcaster,
    l10n,
    broadcasterFirebaseId,
    broadcasterName,
    broadcasterStreamingId,
    liveStreamingMessageId,
  } = args;

  const routeArgs: LiveStreamingRouteArgs = {
    broadcasterFirebaseId,
    broadcasterName,
    broadcasterStreamingId,
    liveStreamingMessageId,
  };

  // Audience (view-only): no capture devices are acquired, so skip the browser
  // permission prompt entirely and navigate directly. Deliberate web deviation
  // from Flutter's shared `_joinLiveStream` (which requests permission for both
  // roles) — see the file header.
  if (!asBroadcaster) {
    Navigator.navigateToLiveStreaming(routeArgs);
    return;
  }

  try {
    // The web permission request: the browser prompt fired by creating the local
    // tracks (Flutter `:840`). We only need it to surface the prompt, so stop the
    // tracks immediately — the page re-acquires them via Agora.
    const stream = await navigator.mediaDevices.getUserMedia({
      video: true,
      audio: true,
    });
    stream.getTracks().forEach((track) => track.stop());
  } catch (error) {
    // Denied / no device → Flutter `:851-855`: toast + no navigation.
    appLogger.error(
      'camera/mic permission denied; not launching live session',
      'launchLiveStreaming',
      {
        context: { liveStreamingMessageId },
        error: error instanceof Error ? error : new Error(String(error)),
      },
    );
    const message = l10n.community.liveStreamingPage.permissionDeniedToast.replace(
      /\{permission\}/g,
      l10n.community.liveStreamingPage.permissionDeniedDevices,
    );
    toast.error(message);
    return;
  }

  // Grant → navigate (Flutter `:856-863`).
  Navigator.navigateToLiveStreaming(routeArgs);
}
